import { Ref, ref, computed } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import { formatSize } from '@/renderer/scripts/utils/tools/index';
type Handler = {
  quotaLimited: Ref<boolean>;
  capacity: Ref<string>;
  size: Ref<string>;
  avaliableSpaceSize: Ref<string>;
  message: Ref<string>;
  linkName: Ref<string>;
  isSharedSpaceSize: Ref<boolean>;
  methods: {
    getPeronalSpaceQuota: () => Promise<void>;
    getTeamSpaceQuota: () => Promise<void>;
    getGroupSpaceQuota: (spaceOrgId: number) => Promise<void>;
  };
};
export default function handler(teamId: Ref<number>): Handler {
  const quotaLimited = ref(false);
  const capacity = ref('0B');
  const size = ref('0B');
  const avaliableSpaceSize = ref('0B');
  // 当前用户是否管理员
  const currentUserIsAdmin = computed(() => store.getters.userBaseInfo.role !== 'user');
  // 显示文案
  const message = ref(i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时向管理员申请扩容'));
  // 跳转链接 用户管理页或团队管理页
  const linkName = ref('');
  // 是否共享企业剩余额度
  const isSharedSpaceSize = ref(false);
  const methods = {
    initData() {
      message.value = '';
      linkName.value = '';
      isSharedSpaceSize.value = false;
    },
    // 查询个人空间容量
    async getPeronalSpaceQuota() {
      methods.initData();
      try {
        const res = await smh.userAPI.hasPersonalSpace();
        const { hasPersonalSpace, capacity: capacityStr, availableSpace = '0', size: sizeData = '0' } = res.data;
        const sizeInfo = formatSize(sizeData);
        let availableSpaceData = +(availableSpace as string) < 0 ? '0' : availableSpace;
        let capacityData = capacityStr as string;
        size.value = `${sizeInfo.size}${sizeInfo.unit}`;
        // 配额为null时代表共享企业剩余可用额度
        if (hasPersonalSpace && capacityStr === null) {
          const { orgCapacity, orgAvailableSpace } = await methods.getOrgAvaliableSpace();
          if (store.getters.editionFlag === 'personal') {
            // 个人版的配额即企业的配额，不存在共享额度的说法
            capacityData = orgCapacity;
            isSharedSpaceSize.value = false;
          } else {
            capacityData = orgAvailableSpace;
            isSharedSpaceSize.value = true;
          }
          availableSpaceData = orgAvailableSpace;
          quotaLimited.value = +orgAvailableSpace <= 0;
        } else {
          quotaLimited.value = +(availableSpace as string) <= 0;
        }
        const capacityInfo = formatSize(capacityData);
        capacity.value = `${capacityInfo.size}${capacityInfo.unit}`;
        const availableSpaceInfo = formatSize(availableSpaceData);
        avaliableSpaceSize.value = `${availableSpaceInfo.size}${availableSpaceInfo.unit}`;
        // 已分配且额度已使用完
        if (quotaLimited.value) {
          if (currentUserIsAdmin.value) {
            // 管理员视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时扩容');
            linkName.value = 'userManagement';
          } else {
            // 普通用户视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时向管理员申请扩容');
            linkName.value = '';
          }
        }
      } catch (e) {
        quotaLimited.value = false;
      }
    },
    // 查询企业空间容量
    async getTeamSpaceQuota() {
      methods.initData();
      try {
        const { data } = await smh.userAPI.getTeamSpace({
          teamId: teamId.value,
        });
        const availableSpace = data.availableSpace || '0';
        let capacityData = data.capacity as string;
        let availableSpaceData = +availableSpace < 0 ? '0' : availableSpace;
        const sizeData = data.size || '0';
        const sizeInfo = formatSize(sizeData);
        size.value = `${sizeInfo.size}${sizeInfo.unit}`;
        // 团队配额为null时代表共享企业剩余可用额度
        if (capacityData === null) {
          const { orgAvailableSpace } = await methods.getOrgAvaliableSpace();
          capacityData = orgAvailableSpace;
          availableSpaceData = orgAvailableSpace;
          quotaLimited.value = +orgAvailableSpace <= 0;
          isSharedSpaceSize.value = true;
        } else {
          quotaLimited.value = +(availableSpace as string) <= 0;
          // 如果这个团队被分配了空间，那么设置他的存储空间
          store.commit('SET_TEAM_SPACE_QUOTA', data.availableSpace);
        }
        const capacityInfo = formatSize(capacityData);
        capacity.value = `${capacityInfo.size}${capacityInfo.unit}`;
        const availableSpaceInfo = formatSize(availableSpaceData);
        avaliableSpaceSize.value = `${availableSpaceInfo.size}${availableSpaceInfo.unit}`;
        // 额度已使用完
        if (quotaLimited.value) {
          if (currentUserIsAdmin.value) {
            // 管理员视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时扩容');
            linkName.value = 'teamManagement';
          } else {
            // 普通用户视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时向管理员申请扩容');
            linkName.value = '';
          }
        }
      } catch (e) {
        quotaLimited.value = false;
      }
    },
    // 查询协作空间容量
    async getGroupSpaceQuota(spaceOrgId: number) {
      try {
        const { data } = await smh.userAPI.getGroupSpace({
          groupId: teamId.value,
          spaceOrgId,
        });
        const capacityData = data.capacity || '0';
        const availableData = data.availableSpace || '0';
        const sizeData = data.size || '0';
        quotaLimited.value = +availableData <= 0;
        const capacityInfo = formatSize(capacityData);
        capacity.value = `${capacityInfo.size}${capacityInfo.unit}`;
        const sizeInfo = formatSize(sizeData);
        size.value = `${sizeInfo.size}${sizeInfo.unit}`;
        // 额度已使用完
        // DOC 看了下这里的逻辑实际已经没在用了，文件列表页没有tips在展示。只用quotaLimited
        if (quotaLimited.value) {
          if (currentUserIsAdmin.value) {
            // 管理员视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时扩容');
            linkName.value = 'teamManagement';
          } else {
            // 普通用户视角
            message.value = i18n_t('当前空间额度已满，上传、编辑等部分功能无法使用，请及时向管理员申请扩容');
            linkName.value = '';
          }
        }
      } catch (e) {
        quotaLimited.value = false;
      }
    },
    // 查询企业剩余可用容量
    async getOrgAvaliableSpace() {
      let spaceInfo = {
        orgAvailableSpace: '0',
        orgCapacity: '0',
        orgSize: '0',
      };
      try {
        const res = await smh.userAPI.getOrgSpace();
        const { availableSpace, capacity, totalFileSize } = res.data;
        spaceInfo = {
          orgAvailableSpace: availableSpace,
          orgCapacity: capacity,
          orgSize: totalFileSize,
        };
      } catch (e) {
        spaceInfo = {
          orgAvailableSpace: '0',
          orgCapacity: '0',
          orgSize: '0',
        };
      }
      return spaceInfo;
    },
  };
  return {
    quotaLimited,
    capacity,
    size,
    avaliableSpaceSize,
    message,
    linkName,
    isSharedSpaceSize,
    methods,
  };
}
